import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getDatabase, ref, onValue } from "firebase/database";
import { getFirebaseApp } from "../../lib/firebaseClient";
import { selectAdminToken, selectSessionId } from "../../admin/sessionSlice";
import {
  wmlBootstrapReport,
  wmlGetState,
  wmlJoin,
  wmlSyncReport,
  wmlVote,
} from "../../admin/functions/wmlApi";
import { setBackButtonUrl } from "../uiSlice";
import confetti from "canvas-confetti";
import {
  mergeWmlDisplay,
  normalizeFirebaseList,
  normalizeWml,
} from "../../lib/wmlSnapshotUtils";
import { fetchThemeData } from "../../admin/themeSlice";
import {
  loadBundledAvatarUrls,
  getWmlAvatarDisplayUrls,
  avatarSrcForKey,
} from "../../lib/wmlAvatars";
import HowToPlayCard from "../rules/HowToPlayCard";
import "./whoMostLikely.css";

function voteStorageKey(sessionId, theme) {
  return `wmlVote:${sessionId}:${theme}`;
}

export default function WhoMostLikely() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const adminToken = useSelector(selectAdminToken);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );
  const { user } = useSelector((state) => state.auth);

  const [joinLoading, setJoinLoading] = useState(true);
  const [joinError, setJoinError] = useState("");
  const [playerId, setPlayerId] = useState(null);

  const [wmlRtdb, setWmlRtdb] = useState(null);
  const [wmlRestPayload, setWmlRestPayload] = useState(null);

  const [voteSubmitting, setVoteSubmitting] = useState(false);
  const [voteError, setVoteError] = useState("");
  const [votedRoundId, setVotedRoundId] = useState(null);
  const [lastVoteChoice, setLastVoteChoice] = useState(null);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [wmlReportId, setWmlReportId] = useState(null);
  const wmlJoinBusyRef = useRef(false);

  const bundledAvatarUrls = useMemo(() => loadBundledAvatarUrls(), []);
  const avatarDisplayUrls = useMemo(
    () => getWmlAvatarDisplayUrls(themeData, bundledAvatarUrls).urls,
    [themeData, bundledAvatarUrls]
  );

  useEffect(() => {
    dispatch(setBackButtonUrl("/select-character"));
  }, [dispatch]);

  useEffect(() => {
    if (!currentTheme || !adminToken) return undefined;
    dispatch(fetchThemeData({ themeId: currentTheme }));
    return undefined;
  }, [dispatch, currentTheme, adminToken]);

  useEffect(() => {
    if (!adminToken) {
      navigate("/login", { replace: true });
    }
  }, [adminToken, navigate]);

  useEffect(() => {
    if (!showRulesModal) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowRulesModal(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showRulesModal]);

  useEffect(() => {
    document.body.classList.toggle("wml-rules-open", showRulesModal);
    return () => {
      document.body.classList.remove("wml-rules-open");
    };
  }, [showRulesModal]);

  const resolveAvatarKey = useCallback(() => {
    const fromState = location.state?.avatarIndex;
    if (typeof fromState === "number" && fromState >= 0) {
      return String(fromState + 1);
    }
    return "1";
  }, [location.state]);

  const resolveDisplayName = useCallback(() => {
    const n = user?.name?.trim();
    if (n) return n;
    const e = user?.email?.trim();
    if (e) return e;
    return "Player";
  }, [user]);

  const resolveUserId = useCallback(() => {
    const raw = user?.userId ?? user?.id;
    if (raw === undefined || raw === null || raw === "") return undefined;
    const value = String(raw).trim();
    return value || undefined;
  }, [user]);

  useEffect(() => {
    if (!adminToken || !sessionId || !currentTheme) {
      setJoinLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function runJoin() {
      if (wmlJoinBusyRef.current) return;
      wmlJoinBusyRef.current = true;
      setJoinError("");
      setJoinLoading(true);
      try {
        const body = {
          token: adminToken,
          currentTheme,
          sessionId,
          displayName: resolveDisplayName(),
          avatarKey: resolveAvatarKey(),
        };
        const uid = resolveUserId();
        if (uid !== undefined) body.userId = uid;

        const data = await wmlJoin(body);
        if (!data.success || !data.playerId) {
          throw new Error(data.message || "Join failed");
        }
        if (!cancelled) {
          setPlayerId(data.playerId);
          if (data.reportId) {
            setWmlReportId(String(data.reportId));
          }
        }
        try {
          const snap = await wmlGetState({
            token: adminToken,
            currentTheme,
            sessionId,
          });
          if (!cancelled && snap?.success) {
            setWmlRestPayload(snap);
          }
        } catch {
          /* ignore */
        }
      } catch (e) {
        if (!cancelled) {
          const roomFull =
            e?.code === "ROOM_FULL" || e?.payload?.code === "ROOM_FULL";
          const userIdRequired =
            e?.code === "USER_ID_REQUIRED" ||
            e?.payload?.code === "USER_ID_REQUIRED";
          const uidRecover = resolveUserId();
          if (
            roomFull &&
            uidRecover !== undefined &&
            adminToken &&
            sessionId &&
            currentTheme
          ) {
            try {
              const snap = await wmlGetState({
                token: adminToken,
                currentTheme,
                sessionId,
              });
              const roster = Array.isArray(snap?.roster) ? snap.roster : [];
              const existingRow = roster.find(
                (p) => String(p.user_id ?? "") === uidRecover
              );
              if (existingRow?.id != null) {
                setPlayerId(Number(existingRow.id));
                setJoinError("");
                if (snap?.success) {
                  setWmlRestPayload(snap);
                }
              } else {
                setJoinError(
                  e.message ||
                    "This session already has the maximum number of players (30)."
                );
              }
            } catch {
              setJoinError(
                e.message ||
                  "This session already has the maximum number of players (30)."
              );
            }
          } else {
            setJoinError(
              userIdRequired
                ? "Please login again to join this game."
                : roomFull
                ? e.message ||
                    "This session already has the maximum number of players (30)."
                : e.message || "Could not join game"
            );
          }
        }
      } finally {
        wmlJoinBusyRef.current = false;
        if (!cancelled) setJoinLoading(false);
      }
    }

    runJoin();
    return () => {
      cancelled = true;
    };
  }, [
    adminToken,
    sessionId,
    currentTheme,
    resolveAvatarKey,
    resolveDisplayName,
    resolveUserId,
  ]);

  useEffect(() => {
    if (
      !adminToken ||
      !currentTheme ||
      !user ||
      !playerId ||
      wmlReportId != null
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await wmlBootstrapReport({
          token: adminToken,
          currentTheme,
          playerId,
          role: user.role,
          userId: user.userId,
          name: user.name,
          gameId: user.gameId || "",
          reportToken: user.token || null,
        });
        const reportId = res?.reportId ?? null;
        if (!cancelled && reportId != null) {
          setWmlReportId(String(reportId));
        }
      } catch (err) {
        console.error("[WML] bootstrapReport", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminToken, currentTheme, user, playerId, wmlReportId]);

  useEffect(() => {
    if (!sessionId || !process.env.REACT_APP_FIREBASE_DATABASE_URL) {
      return undefined;
    }
    const app = getFirebaseApp();
    if (!app) return undefined;
    const db = getDatabase(app);
    const wmlRef = ref(db, `/${sessionId}/wml`);
    const unsub = onValue(
      wmlRef,
      (snap) => {
        setWmlRtdb(normalizeWml(snap.val()));
      },
      (err) => {
        console.warn(
          "[WML] RTDB listener error (check rules / URL):",
          err?.message || err
        );
      }
    );
    return () => unsub();
  }, [sessionId]);

  const wml = useMemo(
    () => mergeWmlDisplay(wmlRtdb, wmlRestPayload),
    [wmlRtdb, wmlRestPayload]
  );

  const phase = wml?.phase || "waiting";
  const currentRound = wml?.currentRound || null;
  const votesSummary = wml?.votesSummary || null;
  const results = wml?.results || null;

  const showWinnerBg =
    phase === "results" && results?.winnerPlayerId != null;
  const noWinnerInResults =
    phase === "results" && results?.winnerPlayerId == null;
  const noVotesInResults =
    noWinnerInResults && Number(results?.voteCount ?? 0) <= 0;

  const skinClass = "who-most-likely--skin-classic";

  useEffect(() => {
    if (!showWinnerBg || results?.winnerPlayerId == null) return undefined;
    const burst = (origin, angle, opts = {}) =>
      confetti({
        particleCount: 72,
        angle,
        spread: 56,
        startVelocity: 42,
        gravity: 0.62,
        origin,
        zIndex: 9999,
        scalar: 1.05,
        ...opts,
      });
    const id = window.setTimeout(() => {
      burst({ x: 0.06, y: 0.88 }, 62);
      window.setTimeout(() => burst({ x: 0.94, y: 0.88 }, 118), 85);
      window.setTimeout(() => {
        burst({ x: 0.5, y: 0.35 }, 90, {
          particleCount: 38,
          spread: 70,
          scalar: 0.85,
          startVelocity: 28,
        });
      }, 260);
    }, 0);
    return () => window.clearTimeout(id);
  }, [showWinnerBg, results?.winnerPlayerId]);

  const pollGameState = useCallback(async () => {
    if (!adminToken || !currentTheme || !sessionId) return;
    try {
      const data = await wmlGetState({
        token: adminToken,
        currentTheme,
        sessionId,
      });
      setWmlRestPayload(data);
    } catch {
      /* ignore transient errors */
    }
  }, [adminToken, currentTheme, sessionId]);

  const lobbyCount = useMemo(() => {
    const roster = wmlRtdb?.roster ?? wmlRestPayload?.roster;
    if (!Array.isArray(roster)) return null;
    return roster.filter(
      (p) => p.is_active === 1 || p.is_active === true
    ).length;
  }, [wmlRtdb?.roster, wmlRestPayload?.roster]);

  const showWaitingCopy =
    phase === "waiting" ||
    (phase !== "results" &&
      phase !== "final" &&
      phase !== "ended" &&
      !currentRound?.promptText);

  useEffect(() => {
    if (!adminToken || !currentTheme || joinLoading || !sessionId) return undefined;
    pollGameState();
    return undefined;
  }, [adminToken, currentTheme, joinLoading, sessionId, pollGameState]);

  useEffect(() => {
    if (!adminToken || !currentTheme || !sessionId || joinLoading) return undefined;
    const onVis = () => {
      if (document.visibilityState === "visible") {
        pollGameState();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [adminToken, currentTheme, sessionId, joinLoading, pollGameState]);

  const promptText =
    phase === "question" || phase === "results"
      ? currentRound?.promptText || ""
      : "";

  const inQuestionPhase = phase === "question" && currentRound?.promptText;
  const [questionTimerTick, setQuestionTimerTick] = useState(0);
  const roundId = currentRound?.roundId ?? null;
  const timerRoundTotalsRef = useRef({});

  useEffect(() => {
    if (!inQuestionPhase || wml?.questionDeadlineMs == null) {
      return undefined;
    }
    const id = window.setInterval(
      () => setQuestionTimerTick((t) => t + 1),
      1000
    );
    return () => window.clearInterval(id);
  }, [inQuestionPhase, wml?.questionDeadlineMs]);

  const questionSecondsLeft = useMemo(() => {
    const d = Number(wml?.questionDeadlineMs);
    if (!inQuestionPhase || !Number.isFinite(d)) return null;
    return Math.max(0, Math.ceil((d - Date.now()) / 1000));
  }, [inQuestionPhase, wml?.questionDeadlineMs, questionTimerTick]);
  useEffect(() => {
    if (roundId == null || questionSecondsLeft == null) return;
    if (!timerRoundTotalsRef.current[roundId]) {
      timerRoundTotalsRef.current[roundId] = Math.max(1, questionSecondsLeft);
    }
  }, [roundId, questionSecondsLeft]);
  const questionTimerTotalSeconds = useMemo(() => {
    if (roundId != null) {
      const seen = Number(timerRoundTotalsRef.current[roundId]);
      if (Number.isFinite(seen) && seen > 0) return seen;
    }
    const configured = Number(themeData?.wmlQuestionTimerSeconds);
    if (Number.isFinite(configured) && configured > 0) return configured;
    return questionSecondsLeft != null ? Math.max(1, questionSecondsLeft) : 1;
  }, [themeData?.wmlQuestionTimerSeconds, roundId, questionSecondsLeft]);
  const questionTimerProgress = useMemo(() => {
    if (questionSecondsLeft == null) return 0;
    const ratio = questionSecondsLeft / questionTimerTotalSeconds;
    return Math.max(0, Math.min(1, ratio));
  }, [questionSecondsLeft, questionTimerTotalSeconds]);
  const hasVotedThisRound =
    roundId != null && votedRoundId != null && votedRoundId === roundId;

  useEffect(() => {
    if (!sessionId || !currentTheme) {
      setVotedRoundId(null);
      setLastVoteChoice(null);
      return;
    }
    if (roundId == null) {
      setVotedRoundId(null);
      setLastVoteChoice(null);
      return;
    }
    try {
      const raw = sessionStorage.getItem(voteStorageKey(sessionId, currentTheme));
      if (!raw) {
        setVotedRoundId(null);
        setLastVoteChoice(null);
        return;
      }
      const parsed = JSON.parse(raw);
      if (Number(parsed.roundId) === Number(roundId)) {
        setVotedRoundId(roundId);
        setLastVoteChoice(parsed.chosenPlayerId);
      } else {
        setVotedRoundId(null);
        setLastVoteChoice(null);
      }
    } catch {
      setVotedRoundId(null);
      setLastVoteChoice(null);
    }
  }, [sessionId, currentTheme, roundId]);

  const handleVote = async (chosenPlayerId) => {
    if (!adminToken || !currentTheme || !playerId || voteSubmitting) return;
    setVoteError("");
    setVoteSubmitting(true);
    try {
      await wmlVote({
        token: adminToken,
        currentTheme,
        voterPlayerId: playerId,
        chosenPlayerId,
        sessionId,
      });
      setVotedRoundId(roundId);
      setLastVoteChoice(chosenPlayerId);
      try {
        sessionStorage.setItem(
          voteStorageKey(sessionId, currentTheme),
          JSON.stringify({ roundId, chosenPlayerId })
        );
      } catch {
        /* ignore */
      }
    } catch (e) {
      setVoteError(e.message || "Vote failed");
    } finally {
      setVoteSubmitting(false);
    }
  };

  const roundOptions = useMemo(
    () => normalizeFirebaseList(currentRound?.options),
    [currentRound?.options]
  );

  const nameByPlayerId = useMemo(() => {
    const m = {};
    roundOptions.forEach((o) => {
      if (o && o.playerId != null) m[o.playerId] = o.displayName || "";
    });
    return m;
  }, [roundOptions]);

  const winnerInfo = useMemo(() => {
    const wid = results?.winnerPlayerId;
    if (wid == null) return null;
    const opt = roundOptions.find((o) => o.playerId === wid);
    if (opt) {
      return {
        name: opt.displayName || `Player ${wid}`,
        avatarKey: opt.avatarKey,
      };
    }
    const lb = normalizeFirebaseList(results?.leaderboard);
    const row = lb.find((r) => r.playerId === wid);
    if (row) {
      return {
        name: row.displayName || `Player ${wid}`,
        avatarKey: row.avatarKey,
      };
    }
    return {
      name: nameByPlayerId[wid] || `Player ${wid}`,
      avatarKey: "1",
    };
  }, [results, roundOptions, nameByPlayerId]);

  const winnerVoteCount = useMemo(() => {
    const tallies = normalizeFirebaseList(results?.tallies);
    const wid = results?.winnerPlayerId;
    if (wid == null) return null;
    const row = tallies.find((t) => t.playerId === wid);
    return row != null ? row.count : null;
  }, [results?.tallies, results?.winnerPlayerId]);

  const runnerUpTallies = useMemo(() => {
    const tallies = normalizeFirebaseList(results?.tallies);
    const wid = results?.winnerPlayerId;
    if (!tallies.length || wid == null) return [];
    return tallies
      .filter((t) => t.playerId !== wid)
      .sort((a, b) => Number(b.count) - Number(a.count))
      .slice(0, 3);
  }, [results?.tallies, results?.winnerPlayerId]);

  const topTiedTallies = useMemo(() => {
    if (!noWinnerInResults) return [];
    const tallies = normalizeFirebaseList(results?.tallies).map((t) => ({
      playerId: t?.playerId,
      count: Number(t?.count),
    }));
    const validTallies = tallies.filter(
      (t) => t.playerId != null && Number.isFinite(t.count) && t.count > 0
    );
    if (!validTallies.length) return [];
    const maxCount = validTallies.reduce((m, t) => Math.max(m, t.count), 0);
    return validTallies.filter((t) => t.count === maxCount);
  }, [noWinnerInResults, results?.tallies]);

  const leaderboardRows = useMemo(
    () => normalizeFirebaseList(results?.leaderboard),
    [results?.leaderboard]
  );
  const hasLeaderboard = leaderboardRows.length > 0;

  const myLeaderboardPoints = useMemo(() => {
    const row = leaderboardRows.find(
      (r) => Number(r.playerId) === Number(playerId)
    );
    return row != null ? Number(row.points) || 0 : 0;
  }, [leaderboardRows, playerId]);

  const wmlReportEndedSentRef = useRef(false);
  const lastReportPointsRef = useRef(undefined);

  useEffect(() => {
    wmlReportEndedSentRef.current = false;
    lastReportPointsRef.current = undefined;
  }, [sessionId, currentTheme, playerId]);

  useEffect(() => {
    if (!user || playerId == null || !currentTheme || !sessionId) return;

    const sync = async (points) => {
      try {
        const res = await wmlSyncReport({
          token: adminToken,
          currentTheme,
          playerId,
          role: user.role,
          userId: user.userId,
          name: user.name,
          gameId: user.gameId || "",
          points,
          time: "00:00:00",
          reportToken: user.token || null,
        });
        if (res?.reportId != null) {
          setWmlReportId(String(res.reportId));
        }
      } catch (err) {
        console.error("[WML] syncReport", err);
      }
    };

    if (phase === "ended" && hasLeaderboard) {
      if (!wmlReportEndedSentRef.current) {
        wmlReportEndedSentRef.current = true;
        sync(myLeaderboardPoints);
      }
      return;
    }

    if (lastReportPointsRef.current === myLeaderboardPoints) return;
    lastReportPointsRef.current = myLeaderboardPoints;

    if (phase === "waiting" && myLeaderboardPoints === 0) return;

    sync(myLeaderboardPoints);
  }, [
    user,
    adminToken,
    playerId,
    currentTheme,
    phase,
    hasLeaderboard,
    myLeaderboardPoints,
  ]);

  const promptLive =
    (phase === "question" || phase === "results") && Boolean(promptText);
  const showQuestionCard = showWaitingCopy || promptLive;

  useEffect(() => {
    if (location.state?.fromLeaderboard) return;
    if (phase !== "ended" && phase !== "final") return;
    navigate("/who-most-likely/leaderboard", { replace: true });
  }, [phase, navigate, location.state]);

  if (!adminToken) {
    return null;
  }

  if (themeStatus === "loading" && !currentTheme) {
    return (
      <main
        className={`who-most-likely who-most-likely--centered ${skinClass}`}
      >
        <p className="who-most-likely__status">Loading…</p>
      </main>
    );
  }

  if (!currentTheme) {
    navigate("/login", { replace: true });
    return null;
  }

  if (joinLoading) {
    return (
      <main
        className={`who-most-likely who-most-likely--centered ${skinClass}`}
      >
        <p className="who-most-likely__status">Joining game…</p>
      </main>
    );
  }

  if (joinError) {
    return (
      <main
        className={`who-most-likely who-most-likely--centered ${skinClass}`}
      >
        <p className="who-most-likely__error">{joinError}</p>
      </main>
    );
  }

  return (
    <main className={["who-most-likely", skinClass].filter(Boolean).join(" ")}>
      <button
        type="button"
        className="wml-howto-trigger"
        onClick={() => setShowRulesModal(true)}
      >
        How to Play
      </button>

      <div className="who-most-likely__layout">
        <div className="who-most-likely__stage">
          <div className="who-most-likely__card who-most-likely__card--title">
            <p className="who-most-likely__card-text">Who is most likely to</p>
          </div>

          {showQuestionCard ? (
            <div
              key={`wml-q-${roundId ?? phase}`}
              className={[
                "who-most-likely__card who-most-likely__card--question",
                "who-most-likely__card--question--enter",
                promptLive ? "who-most-likely__card--question--live" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {showWaitingCopy && (
                <div className="who-most-likely__waiting-block">
                  <p className="who-most-likely__card-text who-most-likely__card-text--question who-most-likely__waiting-title">
                    Waiting for the host…
                  </p>
                  {lobbyCount != null ? (
                    <p className="who-most-likely__lobby-count">
                      Players connected: {lobbyCount}
                    </p>
                  ) : null}
                </div>
              )}

              {promptLive ? (
                <p className="who-most-likely__card-text who-most-likely__card-text--question">
                  {promptText}
                </p>
              ) : null}
            </div>
          ) : null}

          {questionSecondsLeft != null ? (
            <div
              className={[
                "who-most-likely__question-timer",
                questionSecondsLeft > 0 && questionSecondsLeft <= 5
                  ? "who-most-likely__question-timer--urgent"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{
                "--wml-timer-progress": String(questionTimerProgress),
              }}
              aria-live="polite"
              aria-label={`${questionSecondsLeft} seconds left`}
            >
              <div className="who-most-likely__question-timer-track">
                <div className="who-most-likely__question-timer-thumb" aria-hidden>
                  {questionSecondsLeft}
                </div>
              </div>
            </div>
          ) : null}

          {showWinnerBg && winnerInfo ? (
            <div className="who-most-likely__winner-strip">
              <div className="who-most-likely__winner-orbit-wrap">
                <div className="who-most-likely__winner-accent-ring" aria-hidden />
                {winnerVoteCount != null ? (
                  <div
                    className="who-most-likely__winner-points-bubble"
                    aria-label={String(winnerVoteCount)}
                  >
                    {winnerVoteCount}
                  </div>
                ) : null}
                <div className="who-most-likely__winner-avatar-ring">
                  <img
                    src={avatarSrcForKey(winnerInfo.avatarKey, avatarDisplayUrls)}
                    alt=""
                  />
                </div>
              </div>
              <p className="who-most-likely__winner-name">{winnerInfo.name}</p>
            </div>
          ) : null}

          {showWinnerBg &&
          runnerUpTallies.length > 0 &&
          !hasLeaderboard ? (
            <div className="who-most-likely__runner-ups">
              {runnerUpTallies.map((t) => {
                const opt = roundOptions.find((o) => o.playerId === t.playerId);
                const src = avatarSrcForKey(opt?.avatarKey, avatarDisplayUrls);
                return (
                  <div
                    key={t.playerId}
                    className="who-most-likely__runner-up"
                  >
                    <div className="who-most-likely__runner-up-ring">
                      <img src={src} alt="" />
                    </div>
                    <p className="who-most-likely__runner-up-name">
                      {nameByPlayerId[t.playerId] || opt?.displayName || "—"}
                    </p>
                    <span className="who-most-likely__runner-up-count">
                      {t.count}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}

          {noWinnerInResults &&
          topTiedTallies.length > 0 &&
          !hasLeaderboard ? (
            <div className="who-most-likely__runner-ups">
              {topTiedTallies.map((t) => {
                const opt = roundOptions.find((o) => o.playerId === t.playerId);
                const src = avatarSrcForKey(opt?.avatarKey, avatarDisplayUrls);
                return (
                  <div
                    key={t.playerId}
                    className="who-most-likely__runner-up"
                  >
                    <div className="who-most-likely__runner-up-ring">
                      <img src={src} alt="" />
                    </div>
                    <p className="who-most-likely__runner-up-name">
                      {nameByPlayerId[t.playerId] || opt?.displayName || "—"}
                    </p>
                    <span className="who-most-likely__runner-up-count">
                      {t.count}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}

          {noWinnerInResults ? (
            <p className="who-most-likely__post-vote">
              {noVotesInResults
                ? "No one voted before time ended"
                : "It's a tie this round"}
            </p>
          ) : null}
        </div>

        {inQuestionPhase ? (
          <div className="who-most-likely__vote-block">
            <div className="who-most-likely__vote-column">
              <div
                className="who-most-likely__avatars"
                key={roundId ?? "avatars"}
              >
                {roundOptions.map((opt, idx) => {
                  const src = avatarSrcForKey(opt.avatarKey, avatarDisplayUrls);
                  const disabled =
                    voteSubmitting || hasVotedThisRound || !opt.playerId;
                  const selected =
                    hasVotedThisRound &&
                    lastVoteChoice != null &&
                    opt.playerId === lastVoteChoice;
                  return (
                    <div
                      key={opt.playerId}
                      className="who-most-likely__avatar-enter"
                      style={{
                        animationDelay: `${idx * 0.12}s`,
                      }}
                    >
                      <button
                        type="button"
                        className={[
                          "who-most-likely__avatar-slot who-most-likely__avatar-slot--btn",
                          selected
                            ? "who-most-likely__avatar-slot--selected"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        disabled={disabled}
                        onClick={() => handleVote(opt.playerId)}
                      >
                        <div className="who-most-likely__avatar-ring who-most-likely__avatar-ring--pop">
                          <img src={src} alt="" />
                        </div>
                        <p className="who-most-likely__avatar-name">
                          {opt.displayName || "—"}
                        </p>
                      </button>
                    </div>
                  );
                })}
              </div>
              {voteError ? (
                <p className="who-most-likely__error who-most-likely__error--inline">
                  {voteError}
                </p>
              ) : null}
            </div>
            {hasVotedThisRound ? (
              <p className="who-most-likely__post-vote">
                Waiting for results…
                {votesSummary &&
                typeof votesSummary.submittedCount === "number" &&
                typeof votesSummary.expectedCount === "number" ? (
                  <span className="who-most-likely__vote-progress">
                    {" "}
                    ({votesSummary.submittedCount}/{votesSummary.expectedCount}{" "}
                    voted)
                  </span>
                ) : null}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {showRulesModal ? (
        <div
          className="wml-rules-modal"
          role="dialog"
          aria-modal="true"
          aria-label="How to Play"
          onClick={() => setShowRulesModal(false)}
        >
          <div className="wml-rules-modal__panel" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="wml-rules-modal__close"
              aria-label="Close rules"
              onClick={() => setShowRulesModal(false)}
            >
              x
            </button>
            <HowToPlayCard
              rules={Array.isArray(themeData?.rules) ? themeData.rules : []}
              onNext={() => setShowRulesModal(false)}
            />
          </div>
        </div>
      ) : null}
    </main>
  );
}
